import React, { useContext, useEffect, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ACCOUNT_ROLE_MAPPING,
  RoleDefinition,
} from 'src/self-service/constants/UmsConstants';
import { KatButton, KatDropdown, KatInput } from '@amzn/katal-react';
import Spacer from 'src/components/Layout/Spacer';
import { AuthContext } from 'src/state/providers/AuthProvider';
import {
  IdentityTypes,
  ResourceAttributes,
} from 'src/state/slice/models/CaseLifecycleTypes';
import {
  ADD_USER_PAGE,
  VALIDATION,
} from 'src/self-service/constants/SelfServiceConstants';
import styled from 'styled-components';
import sortBy from 'lodash/sortBy';

/**
 * Responsible to generate AccountId dropdown.
 * @param resourceAttributes the resourceAttributes obtained from GetUserAuthorized Resources.
 * @returns Eg: [{'name': 'Herdfordshire', 'value': 'AUTPKRTEEW'}]
 */
function generateAccountIdDropdown(resourceAttributes: ResourceAttributes[]) {
  const accountIdDropdown = resourceAttributes
    .map((resourceAttribute) => {
      const accountMapping = ACCOUNT_ROLE_MAPPING[resourceAttribute.id];
      if (!accountMapping) {
        return;
      }
      return {
        name: resourceAttribute.attributes.accountName,
        value: resourceAttribute.id,
      };
    })
    .filter((data) => data);
  return sortBy(accountIdDropdown, (data) => data?.name);
}

export interface IAddUserForm {
  onSubmit: (data: any) => void;
}

export enum ADD_USER_FORM_FIELDS {
  ACCOUNT_ID = 'accountId',
  USER_TYPE = 'userType',
  ROLE_NAME = 'roleName',
  EMAIL = 'email',
  ALIAS = 'alias',
}

const FormLayout = styled.div`
  font-size: 14px;
`;

export const AddUserForm: React.FC<IAddUserForm> = ({ onSubmit }) => {
  const [approvedRolesOption, setApprovedRolesOption] = useState<any>([]);
  const [userTypeOption, setUserTypeOption] = useState<any>([]);

  const validationSchema = yup.object({
    [ADD_USER_FORM_FIELDS.ACCOUNT_ID]: yup
      .string()
      .required(VALIDATION.MANDATORY_FIELD_MESSAGE),
    [ADD_USER_FORM_FIELDS.USER_TYPE]: yup
      .string()
      .required(VALIDATION.MANDATORY_FIELD_MESSAGE),
    [ADD_USER_FORM_FIELDS.ROLE_NAME]: yup
      .string()
      .oneOf(approvedRolesOption?.map((option) => option.name))
      .required(VALIDATION.MANDATORY_FIELD_MESSAGE),
    [ADD_USER_FORM_FIELDS.EMAIL]: yup.lazy(() => {
      if (userTypeWatchedValue == IdentityTypes.EXTERNAL_CUSTOMER_EMAIL) {
        return yup
          .string()
          .email(VALIDATION.INVALID_EMAIL_MESSAGE)
          .required(VALIDATION.MANDATORY_FIELD_MESSAGE);
      }
      return yup.string().notRequired();
    }),
    [ADD_USER_FORM_FIELDS.ALIAS]: yup.lazy(() => {
      if (userTypeWatchedValue == IdentityTypes.INTERNAL_USER_ALIAS) {
        return yup.string().required(VALIDATION.MANDATORY_FIELD_MESSAGE);
      }
      return yup.string().notRequired();
    }),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userType: '',
      accountId: '',
      roleName: '',
      email: undefined,
      alias: undefined,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const userTypeWatchedValue = useWatch({
    control,
    name: ADD_USER_FORM_FIELDS.USER_TYPE,
  });
  const accountIdWatchedValue = useWatch({
    control,
    name: ADD_USER_FORM_FIELDS.ACCOUNT_ID,
  });

  useEffect(() => {
    if (accountIdWatchedValue) {
      const userTypeOptions = [
        { name: 'External', value: IdentityTypes.EXTERNAL_CUSTOMER_EMAIL },
        { name: 'Internal', value: IdentityTypes.INTERNAL_USER_ALIAS },
      ];
      const account = ACCOUNT_ROLE_MAPPING[accountIdWatchedValue];
      if (!account) {
        return;
      }
      const filteredUserTypeOptions = userTypeOptions.filter(
        (userTypeOption) => account.roles[userTypeOption.value],
      );
      setUserTypeOption(filteredUserTypeOptions);
    }

    if (accountIdWatchedValue && userTypeWatchedValue) {
      const roles = ACCOUNT_ROLE_MAPPING[accountIdWatchedValue]?.roles;
      if (roles) {
        const selectedRoles: RoleDefinition[] = roles[userTypeWatchedValue];
        const roleDropdown = selectedRoles?.map((role) => {
          return {
            name: role.name,
            value: role.name,
          };
        });
        setApprovedRolesOption(roleDropdown);
      }
    }
  }, [accountIdWatchedValue, userTypeWatchedValue]);

  const { accountAttributes } = useContext(AuthContext);
  const accountIdOption = generateAccountIdDropdown(accountAttributes);

  return (
    <FormLayout>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={ADD_USER_FORM_FIELDS.ACCOUNT_ID}
          control={control}
          render={({ field }) => (
            <>
              <KatDropdown
                {...field}
                label={ADD_USER_PAGE.FIELD_ACCOUNT_ID_LABEL}
                value={field.value!}
                options={accountIdOption}
                state={Boolean(errors.accountId) ? 'error' : undefined}
                stateLabel={errors.accountId?.message}
                constraintLabel={
                  ADD_USER_PAGE.FIELD_ACCOUNT_ID_CONSTRAINT_LABEL
                }
                searchable
              />
              <Spacer height="1rem" />
            </>
          )}
        />
        <Controller
          name={ADD_USER_FORM_FIELDS.USER_TYPE}
          control={control}
          render={({ field }) => (
            <>
              <KatDropdown
                {...field}
                label={ADD_USER_PAGE.FIELD_USER_TYPE_LABEL}
                value={field.value!}
                options={userTypeOption}
                state={Boolean(errors.userType) ? 'error' : undefined}
                stateLabel={errors.userType?.message}
                constraintLabel={ADD_USER_PAGE.FIELD_USER_TYPE_CONSTRAINT_LABEL}
              />
              <Spacer height="1rem" />
            </>
          )}
        />

        <Controller
          name={ADD_USER_FORM_FIELDS.ROLE_NAME}
          control={control}
          render={({ field }) => (
            <>
              <KatDropdown
                {...field}
                label={ADD_USER_PAGE.FIELD_ROLE_LABEL}
                value={field.value!}
                options={approvedRolesOption}
                state={Boolean(errors.roleName) ? 'error' : undefined}
                stateLabel={errors.roleName?.message?.toString()}
                constraintLabel={ADD_USER_PAGE.FIELD_ROLE_CONSTRAINT_LABEL}
              />
              <Spacer height="1rem" />
            </>
          )}
        />

        {userTypeWatchedValue == IdentityTypes.INTERNAL_USER_ALIAS && (
          <Controller
            name={ADD_USER_FORM_FIELDS.ALIAS}
            control={control}
            render={({ field }) => (
              <>
                <KatInput
                  {...field}
                  value={field.value!}
                  label={ADD_USER_PAGE.FIELD_INTERNAL_EMAIL_ALIAS_LABEL}
                  state={Boolean(errors.alias) ? 'error' : undefined}
                  stateLabel={errors.alias?.message}
                  constraintLabel={
                    ADD_USER_PAGE.FIELD_INTERNAL_EMAIL_ALIAS_CONSTRAINT_LABEL
                  }
                />
                <Spacer height="1rem" />
              </>
            )}
          />
        )}
        {userTypeWatchedValue == IdentityTypes.EXTERNAL_CUSTOMER_EMAIL && (
          <Controller
            name={ADD_USER_FORM_FIELDS.EMAIL}
            control={control}
            render={({ field }) => (
              <>
                <KatInput
                  {...field}
                  value={field.value!}
                  label={ADD_USER_PAGE.FIELD_EXTERNAL_EMAIL_LABEL}
                  state={Boolean(errors.email) ? 'error' : undefined}
                  stateLabel={errors.email?.message}
                  constraintLabel={
                    ADD_USER_PAGE.FIELD_EXTERNAL_EMAIL_CONSTRAINT_LABEL
                  }
                />
                <Spacer height="1rem" />
              </>
            )}
          />
        )}

        <KatButton type="submit">{ADD_USER_PAGE.SUBMIT_BUTTON_LABEL}</KatButton>
      </form>
    </FormLayout>
  );
};
